import { getQueryString, queryString } from '@/lib/util';
import { CourseLibraryDefaultQueryString } from '@/pages/CourseLibrary/CourseLibraryRoutes';
import { LibraryElearningCategoriesInterface } from '@/pages/CourseLibrary/types';
import { Select, Spinner } from 'elmo-elements';
import { SelectPropsOption } from 'elmo-elements/dist/Select/type';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import './CategoryFilter.scss';

const defaultCategory = {
  label: 'All Categories',
  value: '',
};

interface ICategoryFilterProps {
  allowedCategories: LibraryElearningCategoriesInterface[];
  isLibraryDataLoading: boolean;
  isNewList?: boolean;
}

export const CategoryFilter = ({
  allowedCategories,
  isLibraryDataLoading,
  isNewList = false,
}: ICategoryFilterProps): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const currentQueryString = getQueryString(
    location.search,
    CourseLibraryDefaultQueryString,
  );
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);

  const categoryOptions = useMemo<SelectPropsOption[]>(() => {
    const options: SelectPropsOption[] = allowedCategories
      .map((category: LibraryElearningCategoriesInterface) => ({
        value: String(category.categoryId),
        label: category.categoryName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (options.length > 0) {
      return [{ ...defaultCategory }, ...options];
    }

    return [{ ...defaultCategory }];
  }, [allowedCategories]);

  useEffect(() => {
    if (currentQueryString.category && categoryOptions) {
      const preSelectedCategory = categoryOptions.find(
        (category: SelectPropsOption) =>
          category.value === currentQueryString.category,
      );

      if (preSelectedCategory) {
        setSelectedCategory({
          label: String(preSelectedCategory.label),
          value: preSelectedCategory.value,
        });
      }
    }
  }, [categoryOptions, currentQueryString.category]);

  if (isLibraryDataLoading) {
    return <Spinner />;
  }

  const buildCategoryUrlParams = (categoryValue: string) => {
    const currentParams = getQueryString(location.search);
    const paramKey = isNewList ? 'categoryId' : 'category';

    const newParams: Record<string, string | number> = {
      ...currentParams,
      page: 1,
    };

    if (categoryValue) {
      newParams[paramKey] = categoryValue;
    } else {
      delete newParams[paramKey];
    }

    return newParams;
  };

  const handleCategoryFilterChange = (category: SelectPropsOption): void => {
    const urlParams = buildCategoryUrlParams(category.value);

    setSelectedCategory({
      value: String(category.value),
      label: String(category.label),
    });

    history.push({
      pathname: location.pathname,
      search: queryString.stringify(urlParams),
    });
  };

  return (
    <div className="category-filter">
      <Select
        options={categoryOptions}
        value={selectedCategory}
        onChange={handleCategoryFilterChange}
      />
    </div>
  );
};
